/**
 * difyClient.ts
 * 同一オリジンのサーバー側プロキシ経由でDify APIを呼び出す。
 * レスポンスはコールバックで受け取るストリーミング対応。
 */
import { getUserId, setUserId } from "./persistentState";
import { getApiToken, getApiUrl } from "./textData";

const MAX_DIFY_ID_LENGTH = 128;
let conversationId: string = (
    localStorage.getItem("esp8266_conversation_id") || ""
).slice(0, MAX_DIFY_ID_LENGTH);
let userRegistrationPromise: Promise<string> | null = null;
const activeRequests = new Set<AbortController>();
const DIFY_TIMEOUT_MS = 30_000;
const MAX_DIFY_STREAM_BYTES = 256 * 1024;
const MAX_DIFY_ANSWER_CHARS = 64 * 1024;

interface DifyCallOptions {
    user?: string;
    conversationId?: string;
    persistConversation?: boolean;
}

interface DifyRequestBody {
    inputs: Record<string, never>;
    query: string;
    response_mode: "streaming";
    conversation_id?: string;
    user: string;
    files: Array<{
        type: "image";
        transfer_method: "remote_url";
        url: string;
    }>;
}

export function getConversationId(): string {
    return conversationId;
}

export function setConversationId(id: string): void {
    conversationId = id.slice(0, MAX_DIFY_ID_LENGTH);
    try {
        localStorage.setItem("esp8266_conversation_id", conversationId);
    } catch {
        // 容量・プライバシーモード制約でもAPI応答自体は継続する。
    }
}

export function clearConversationId(): void {
    conversationId = "";
    localStorage.removeItem("esp8266_conversation_id");
}

export function cancelActiveDifyRequests(): void {
    for (const controller of activeRequests) controller.abort();
    activeRequests.clear();
}

/**
 * Dify.ai APIを呼び出し、レスポンスのフルテキストをPromiseで返す。
 * streaming=trueの場合はSSEストリームを読み取って連結する。
 */
export async function callDifyAPI(
    message: string,
    onChunk?: (partial: string) => void,
    options: DifyCallOptions = {},
): Promise<string> {
    const apiUrl = getApiUrl();
    const apiToken = getApiToken();

    if (!apiToken) {
        throw new Error(
            "API token is not configured. Please set it in ESP8266 settings.",
        );
    }

    const controller = new AbortController();
    activeRequests.add(controller);
    const timeoutId = setTimeout(() => controller.abort(), DIFY_TIMEOUT_MS);

    try {
        let body: DifyRequestBody = {
            inputs: {},
            query: message,
            response_mode: "streaming",
            conversation_id:
                options.conversationId !== undefined
                    ? options.conversationId || undefined
                    : conversationId || undefined,
            user: options.user || getUserId(),
            files: [
                {
                    type: "image",
                    transfer_method: "remote_url",
                    url: "https://cloud.dify.ai/logo/logo-site.png",
                },
            ],
        };

        const doFetch = (b: typeof body) =>
            fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: apiToken.startsWith("Bearer ")
                        ? apiToken
                        : `Bearer ${apiToken}`,
                },
                body: JSON.stringify(b),
                signal: controller.signal,
            });

        let resp = await doFetch(body);

        // conversation_id が古くて 404 になった場合はクリアしてリトライ
        if (!resp.ok && resp.status === 404 && body.conversation_id) {
            clearConversationId();
            body = { ...body, conversation_id: undefined };
            resp = await doFetch(body);
        }

        if (!resp.ok) {
            const errText = await resp.text().catch(() => "");
            throw new Error(`Dify API error ${resp.status}: ${errText}`);
        }

        // SSEストリームを読む
        const reader = resp.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let fullAnswer = "";
        let buffer = "";
        let receivedBytes = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            receivedBytes += value.byteLength;
            if (receivedBytes > MAX_DIFY_STREAM_BYTES) {
                throw new Error("Dify response exceeded the size limit");
            }
            buffer += decoder.decode(value, { stream: true });

            // SSEは行単位 "data: {...}\n\n"
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? ""; // 未完結行を保持

            for (const line of lines) {
                if (!line.startsWith("data: ")) continue;
                const jsonStr = line.slice(6).trim();
                if (jsonStr === "[DONE]") continue;
                let event: Record<string, unknown>;
                try {
                    event = JSON.parse(jsonStr) as Record<string, unknown>;
                } catch {
                    // 無効なJSONはスキップ
                    continue;
                }
                // conversation_idを保存
                if (
                    event.conversation_id &&
                    typeof event.conversation_id === "string"
                ) {
                    if (options.persistConversation !== false) {
                        setConversationId(event.conversation_id);
                    }
                }
                // テキストチャンクを収集
                if (
                    (event.event === "message" ||
                        event.event === "agent_message") &&
                    typeof event.answer === "string"
                ) {
                    const chunk = event.answer;
                    if (
                        fullAnswer.length + chunk.length >
                        MAX_DIFY_ANSWER_CHARS
                    ) {
                        throw new Error("Dify answer exceeded the size limit");
                    }
                    fullAnswer += chunk;
                    onChunk?.(chunk);
                }
            }
        }

        return fullAnswer;
    } catch (error) {
        // サイズ超過やSSE解析失敗でもfetch bodyを放置しない。
        controller.abort();
        throw error;
    } finally {
        clearTimeout(timeoutId);
        activeRequests.delete(controller);
    }
}

export function ensureUserId(): Promise<string> {
    const currentUserId = getUserId();
    if (currentUserId !== "newuser") return Promise.resolve(currentUserId);
    if (userRegistrationPromise) return userRegistrationPromise;

    userRegistrationPromise = callDifyAPI("newuser", undefined, {
        user: "newuser",
        conversationId: "",
        persistConversation: false,
    })
        .then(answer => {
            const assignedUserId = answer.trim();
            if (assignedUserId) setUserId(assignedUserId);
            clearConversationId();
            return getUserId();
        })
        .finally(() => {
            userRegistrationPromise = null;
        });

    return userRegistrationPromise;
}
